"use client";

import {
  useState,
  useEffect,
  useCallback,
  startTransition,
  useRef,
  type ChangeEvent,
  type ReactNode,
} from "react";
import Link from "next/link";
import { getMuhurat } from "@/lib/api/muhurat";
import type { MuhuratResponse } from "@/lib/api/muhurat";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { Collapsible } from "@ishvaram/ui/Collapsible";

function toDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const DAY_NAMES_HI: Record<string, string> = {
  Monday: "सोमवार",
  Tuesday: "मंगलवार",
  Wednesday: "बुधवार",
  Thursday: "गुरुवार",
  Friday: "शुक्रवार",
  Saturday: "शनिवार",
  Sunday: "रविवार",
};

const WEEKLY_RAHU_ROWS = [
  { day: "Monday", hi: "सोमवार", seg: "2nd", time: "7:30 - 9:00 AM" },
  { day: "Saturday", hi: "शनिवार", seg: "3rd", time: "9:00 - 10:30 AM" },
  { day: "Friday", hi: "शुक्रवार", seg: "4th", time: "10:30 - 12:00 PM" },
  { day: "Wednesday", hi: "बुधवार", seg: "5th", time: "12:00 - 1:30 PM" },
  { day: "Thursday", hi: "गुरुवार", seg: "6th", time: "1:30 - 3:00 PM" },
  { day: "Tuesday", hi: "मंगलवार", seg: "7th", time: "3:00 - 4:30 PM" },
  { day: "Sunday", hi: "रविवार", seg: "8th", time: "4:30 - 6:00 PM" },
] as const;

export interface RahuKaalProps {
  defaultCity?: string;
  initialDate?: string;
  initialResult?: MuhuratResponse | null;
  resultOffer: ReactNode;
  crossLinks: ReactNode;
}

export function RahuKaal({
  defaultCity = "New Delhi",
  initialDate,
  initialResult = null,
  resultOffer,
  crossLinks,
}: RahuKaalProps) {
  const [loading, setLoading] = useState(!initialResult);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MuhuratResponse | null>(initialResult);
  const trackedInitialResult = useRef(false);
  const [selectedDate, setSelectedDate] = useState<string>(initialDate ?? toDateString(new Date()));

  const fetchData = useCallback(
    async (dateStr: string) => {
      startTransition(() => {
        setLoading(true);
        setError(null);
      });
      try {
        const response = await getMuhurat(dateStr, defaultCity);
        startTransition(() => setResult(response));
        trackCalculatorUsed({ calculator: "rahu-kaal" });
      } catch (err) {
        startTransition(() =>
          setError(err instanceof Error ? err.message : "Failed to load Rahu Kaal data")
        );
      } finally {
        startTransition(() => setLoading(false));
      }
    },
    [defaultCity]
  );

  useEffect(() => {
    if (initialResult && selectedDate === initialDate && !trackedInitialResult.current) {
      trackedInitialResult.current = true;
      trackCalculatorUsed({ calculator: "rahu-kaal" });
      return;
    }

    fetchData(selectedDate);
  }, [fetchData, initialDate, initialResult, selectedDate]);

  const displayDate = new Date(selectedDate + "T00:00:00");
  const dayName = displayDate.toLocaleDateString("en-IN", { weekday: "long" });
  const dayNameHi = DAY_NAMES_HI[dayName] ?? "";
  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
        <RahuHeader displayDate={displayDate} dayNameHi={dayNameHi} city={defaultCity} />
        <RahuDatePicker selectedDate={selectedDate} onChange={handleDateChange} />

        <div className="min-h-[760px] sm:min-h-[700px]">
          {loading && <RahuLoading />}
          {error && !loading && <RahuError error={error} onRetry={() => fetchData(selectedDate)} />}
          {result && !loading && (
            <RahuResults result={result} dayName={dayName} resultOffer={resultOffer} />
          )}
        </div>

        <RahuSeoContent />

        {crossLinks}
      </div>
    </div>
  );
}

function RahuHeader({
  displayDate,
  dayNameHi,
  city,
}: {
  displayDate: Date;
  dayNameHi: string;
  city: string;
}) {
  return (
    <div className="text-center mb-10">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
        Rahu Kaal Calculator
      </p>
      <h1 className="text-[36px] sm:text-[40px] font-bold leading-tight text-ish-dark font-serif">
        Rahu Kaal Today
      </h1>
      <p className="text-[18px] mt-1 text-ish-muted font-serif">आज का राहु काल</p>
      <p className="mt-2 text-[14px] text-ish-muted">
        {formatDate(displayDate)} &middot; {dayNameHi}
      </p>
      <p className="mt-1 text-[12px] text-ish-faint">
        Calculated for {city}, India using Swiss Ephemeris
      </p>
    </div>
  );
}

function RahuDatePicker({
  selectedDate,
  onChange,
}: {
  selectedDate: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex justify-center mb-8">
      <div className="card-sacred inline-flex items-center gap-3 px-4 py-2.5">
        <label htmlFor="rahu-kaal-date" className="text-[13px] font-semibold text-ish-dark">
          Check for another date:
        </label>
        <input
          id="rahu-kaal-date"
          type="date"
          value={selectedDate}
          onChange={onChange}
          className="input-sacred rounded-lg px-3 py-1.5 text-[14px] text-ish-dark"
        />
      </div>
    </div>
  );
}

function RahuLoading() {
  return (
    <div className="card-sacred p-8 text-center">
      <span className="inline-block w-6 h-6 border-2 border-stone-300 border-t-stone-600 rounded-full animate-spin mb-3" />
      <span className="text-[28px] sm:text-[40px] font-bold block text-ish-dark font-serif">
        Rahu Kaal Today
      </span>
      <span className="mt-1 block font-serif text-[32px] font-bold text-error sm:text-[40px]">
        --:-- -- - --:-- --
      </span>
      <p className="text-[14px] text-ish-muted">Loading Rahu Kaal timings...</p>
    </div>
  );
}

function RahuError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="card-sacred p-6 text-center border-error-tint bg-error/5">
      <p className="text-[14px] text-error">{error}</p>
      <button onClick={onRetry} className="mt-3 text-[13px] font-semibold text-ish-accent">
        Try Again
      </button>
    </div>
  );
}

export function RahuResults({
  result,
  dayName,
  resultOffer,
}: {
  result: MuhuratResponse;
  dayName: string;
  resultOffer: ReactNode;
}) {
  return (
    <div className="space-y-6">
      <PrimaryRahuCard result={result} />
      {resultOffer}
      <SecondaryInauspiciousSlots result={result} />
      <PanchangSummaryCard result={result} />
      <WeeklyRahuReference dayName={dayName} />
      <KundaliCta />
    </div>
  );
}

function PrimaryRahuCard({ result }: { result: MuhuratResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8 text-center border-error/35 bg-error/5">
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-error">
        {result.rahu_kaal.label_hi}
      </span>
      <span className="block text-[13px] font-semibold uppercase tracking-wider mb-3 text-error">
        Rahu Kaal
      </span>
      <span className="text-[32px] sm:text-[40px] font-bold block text-error font-serif">
        {result.rahu_kaal.start} - {result.rahu_kaal.end}
      </span>
      <p className="mt-3 text-[13px] text-error">
        Avoid starting new activities during this period
      </p>
    </div>
  );
}

function SecondaryInauspiciousSlots({ result }: { result: MuhuratResponse }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <SecondarySlotCard
        label="Yamagandam"
        labelHi={result.yamagandam.label_hi}
        time={`${result.yamagandam.start} - ${result.yamagandam.end}`}
        description="Ruled by Yama · Avoid travel"
      />
      <SecondarySlotCard
        label="Gulika Kaal"
        labelHi={result.gulika_kaal.label_hi}
        time={`${result.gulika_kaal.start} - ${result.gulika_kaal.end}`}
        description="Ruled by Saturn · Avoid auspicious work"
      />
    </div>
  );
}

function SecondarySlotCard({
  label,
  labelHi,
  time,
  description,
}: {
  label: string;
  labelHi: string;
  time: string;
  description: string;
}) {
  return (
    <div className="card-sacred p-5 text-center border-error/20 bg-error/2">
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-error">
        {labelHi}
      </span>
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-2 text-error">
        {label}
      </span>
      <span className="text-[20px] font-bold text-error font-serif">{time}</span>
      <p className="mt-2 text-[12px] text-ish-muted">{description}</p>
    </div>
  );
}

function PanchangSummaryCard({ result }: { result: MuhuratResponse }) {
  return (
    <div className="card-sacred p-6 sm:p-8">
      <h2 className="text-[18px] font-bold mb-4 text-ish-dark font-serif">
        Today&apos;s Panchang Summary
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <PanchangFact
          label="Tithi"
          value={result.panchang.tithi.name_en}
          detail={`${result.panchang.tithi.name_hi} · ${pakshaLabel(result)}`}
        />
        <PanchangFact
          label="Nakshatra"
          value={result.panchang.nakshatra.name_en}
          detail={result.panchang.nakshatra.name_hi}
        />
        <PanchangFact
          label="Yoga"
          value={result.panchang.yoga.name_en}
          detail={result.panchang.yoga.name_hi}
        />
        <PanchangFact label="Day" value={result.vara.name_en} detail={result.vara.name_hi} />
      </div>
    </div>
  );
}

function pakshaLabel(result: MuhuratResponse) {
  return result.panchang.tithi.paksha === "shukla" ? "Shukla Paksha" : "Krishna Paksha";
}

function PanchangFact({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wider mb-1 text-ish-faint">
        {label}
      </span>
      <span className="text-[15px] font-bold text-ish-dark">{value}</span>
      <span className="block text-[12px] text-ish-faint">{detail}</span>
    </div>
  );
}

function WeeklyRahuReference({ dayName }: { dayName: string }) {
  return (
    <Collapsible title="Weekly Rahu Kaal Reference" defaultOpen={false}>
      <div className="card-sacred p-6 sm:p-8 mt-0">
        <p className="text-[13px] mb-4 text-ish-muted">
          Approximate times based on 6:00 AM sunrise. Use the calculator above for exact timings.
        </p>
        <div className="space-y-1.5">
          {WEEKLY_RAHU_ROWS.map((row) => (
            <WeeklyRahuRow key={row.day} row={row} isToday={row.day === dayName} />
          ))}
        </div>
      </div>
    </Collapsible>
  );
}

function WeeklyRahuRow({
  row,
  isToday,
}: {
  row: (typeof WEEKLY_RAHU_ROWS)[number];
  isToday: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border ${
        isToday ? "bg-error/6 border-error/25" : "bg-black/2 border-ish-border-light"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`text-[14px] font-semibold ${isToday ? "text-error" : "text-ish-dark"}`}>
          {row.day}
        </span>
        <span className="text-[12px] text-ish-faint">{row.hi}</span>
        {isToday && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/12 text-error">
            TODAY
          </span>
        )}
      </div>
      <div className="text-right">
        <span className={`text-[13px] font-semibold ${isToday ? "text-error" : "text-ish-dark"}`}>
          {row.time}
        </span>
        <span className="text-[11px] ml-1.5 text-ish-faint">({row.seg})</span>
      </div>
    </div>
  );
}

function KundaliCta() {
  return (
    <div className="text-center">
      <Link
        href="/free-kundali"
        className="btn-sacred inline-block font-semibold py-3 px-6 rounded-lg text-[14px]"
      >
        Get Your Free Kundali &rarr;
      </Link>
    </div>
  );
}

function RahuSeoContent() {
  return (
    <Collapsible title="About Rahu Kaal" titleHi="राहु काल के बारे में" defaultOpen={false}>
      <div className="card-sacred p-6 sm:p-8 mt-0">
        <div className="space-y-3 text-[14px] leading-relaxed text-ish-muted">
          <p>
            <strong className="text-ish-dark">Rahu Kaal</strong> (राहु काल) is one of the three
            inauspicious daily periods in Vedic astrology, alongside Yamagandam and Gulika Kaal. It
            lasts approximately 90 minutes each day and is ruled by the shadow planet Rahu — the
            north node of the Moon. Most Hindus avoid starting new ventures, signing documents, or
            beginning journeys during this period.
          </p>
          <p>
            The timing of Rahu Kaal changes every day because it depends on the day of the week and
            your local sunrise and sunset times. Our calculator uses the same astronomical engine
            (Swiss Ephemeris with Lahiri Ayanamsa) as the{" "}
            <Link href="/muhurat/" className="font-semibold text-ish-accent">
              Muhurat Calculator
            </Link>{" "}
            and{" "}
            <Link href="/panchang/" className="font-semibold text-ish-accent">
              Daily Panchang
            </Link>{" "}
            to give you precise, location-aware timings.
          </p>
          <p>
            For a complete picture of auspicious and inauspicious timings throughout the day,
            including hourly Muhurat analysis, visit our{" "}
            <Link href="/muhurat/" className="font-semibold text-ish-accent">
              Shubh Muhurat
            </Link>{" "}
            page.
          </p>
        </div>
      </div>
    </Collapsible>
  );
}
